import { DebugLogger } from '@affine/debug';
import type { Dict, OverridedMixpanel } from 'mixpanel-browser';

const logger = new DebugLogger('product-analytics');

// Release-bound provider switch. A token alone must never activate analytics.
const MIXPANEL_RELEASE_ENABLED = false;

type Middleware = (
  name: string,
  properties?: Record<string, unknown>
) => Record<string, unknown>;

function createMixpanel() {
  let client: OverridedMixpanel | undefined;
  let allowed = false;
  const middlewares = new Set<Middleware>();

  const wrapped = {
    async configure(consentGranted: boolean) {
      allowed = consentGranted && MIXPANEL_RELEASE_ENABLED;
      if (!allowed) {
        client?.reset();
        client = undefined;
        return;
      }
      if (client || !BUILD_CONFIG.MIXPANEL_TOKEN) return;
      const module = await import('mixpanel-browser');
      const provider = module.default;
      provider.init(BUILD_CONFIG.MIXPANEL_TOKEN, {
        track_pageview: false,
        autocapture: false,
        disable_persistence: true,
        ignore_dnt: false,
      });
      client = provider;
      wrapped.init();
    },
    init() {
      if (!allowed || !client) return;
      this.register({
        appVersion: BUILD_CONFIG.appVersion,
        environment: BUILD_CONFIG.appBuildType,
        editorVersion: BUILD_CONFIG.editorVersion,
        isDesktop: BUILD_CONFIG.isElectron,
        distribution: BUILD_CONFIG.distribution,
      });
    },
    register(props: Dict) {
      if (!allowed || !client) return;
      logger.debug('register approved properties');
      client.register(props);
    },
    reset() {
      client?.reset();
    },
    track(eventName: string, properties?: Record<string, any>) {
      if (!allowed || !client) return;
      const safeProperties = Array.from(middlewares).reduce(
        (acc, middleware) => middleware(eventName, acc),
        properties ?? {}
      );
      client.track(eventName, safeProperties);
    },
    middleware(cb: Middleware): () => void {
      middlewares.add(cb);
      return () => middlewares.delete(cb);
    },
    opt_out_tracking() {
      allowed = false;
      client?.reset();
      client = undefined;
    },
    opt_in_tracking() {},
    has_opted_in_tracking() {
      return allowed;
    },
    has_opted_out_tracking() {
      return !allowed;
    },
    identify(_uniqueId?: string) {
      // Identifiers are excluded by the StreamLain telemetry contract.
    },
    get people() {
      return {
        set(_properties: Record<string, unknown>) {
          // Profile fields are excluded by the StreamLain telemetry contract.
        },
      };
    },
    track_pageview(_properties?: { location?: string }) {
      // Page views and URL capture are excluded by default.
    },
  };
  return wrapped;
}

export const mixpanel = createMixpanel();
